import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  getProbUncertainty,
  getSimDriverRaces,
  ParlayLeg,
  ProbUncertaintyBand,
  RaceChaos,
  runSimulationMatchup,
  SimDriverResult,
  SimModel,
  SimulationMatchup,
} from '../services/api';
import { BetValuePanel } from './BetValuePanel';
import { addToSlip, betFromLegs, useSlipCount } from '../utils/slipStore';
import { BookModeToggle } from './BookModeToggle';
import { bookHeadToHead, bookMatchupDriver } from '../utils/bookMode';
import { SimMatchupSummary } from './SimMatchupSummary';
import { bandFor } from '../utils/betValue';
import { DriverDetails } from '../types/predictions';
import { RaceHistoryTable, getModalBodyBg } from './DriverDetailsModal';
import { RaceCharts } from './charts/RaceCharts';
import { OddsToggle, toAmericanOdds } from './OddsToggle';
import { getCellColor } from '../utils/cellColors';
import { maxScreenHeight } from '../utils/viewport';
import { ModalHeader, modalScrollBody, modalScrollPaper, useScrolledDown } from './ModalHeader';

interface SimMatchupModalProps {
  open: boolean;
  onClose: () => void;
  /** Site and as-of date of the simulation the drivers were picked from. */
  site: string;
  asOf: string;
  nSims: number;
  drivers: string[];
  /** Tracks removed from the simulation's ratings (applied to the matchup and race history too). */
  excludeTracks?: string[];
  initialShowOdds?: boolean;
  /** Start with book mode on (follows the Simulator's switch). */
  initialBookMode?: boolean;
  /** The Simulator's model: production or the track-specific layer. */
  model?: SimModel;
  /** The run's race chaos scenario (the matchup is simulated the same way). */
  chaos?: RaceChaos;
  /** The simulated track's key, for the race chart's "This track" filter. */
  trackKey?: string;
  /** The overview run's results for these drivers (finish odds chart). */
  finishOdds?: SimDriverResult[];
}

const formatPct = (p: number): string => {
  const pct = p * 100;
  if (pct > 0 && pct < 0.1) return '<0.1%';
  return `${pct.toFixed(1)}%`;
};

const lastName = (driver: string): string => {
  const parts = driver.replace(/\s+(Jr\.?|Sr\.?|II|III)$/i, '').split(' ');
  return parts[parts.length - 1];
};

/** Head-to-head / group matchup between drivers chosen on the Simulator, plus their race-by-race history. */
export const SimMatchupModal: React.FC<SimMatchupModalProps> = ({
  open,
  onClose,
  site,
  asOf,
  nSims,
  drivers,
  excludeTracks = [],
  initialShowOdds = false,
  initialBookMode = false,
  model = 'base',
  chaos,
  trackKey,
  finishOdds = [],
}) => {
  const theme = useTheme();
  const bodyBg = getModalBodyBg(theme);
  const [showOdds, setShowOdds] = useState(initialShowOdds);
  const [bookMode, setBookMode] = useState(initialBookMode);
  const [scrolled, onScroll] = useScrolledDown();
  const isHeadToHead = drivers.length === 2;
  const key = [...drivers].sort().join('|');
  const excludeKey = [...excludeTracks].sort().join('|');

  const matchup = useQuery<SimulationMatchup, Error>({
    queryKey: ['simMatchup', site, nSims, key, excludeKey, model, chaos],
    queryFn: () => runSimulationMatchup(site, nSims, drivers, excludeTracks, model, chaos),
    enabled: open && drivers.length >= 2,
    staleTime: Infinity,
  });

  const races = useQuery<Record<string, DriverDetails[]>, Error>({
    // Follows the model toggle, so the races below match the odds above.
    queryKey: ['simDriverRaces', site, asOf, key, excludeKey, model],
    queryFn: async () => {
      const rows = await getSimDriverRaces(site, asOf, drivers, excludeTracks, model);
      // Keyed in the matchup's driver order, so chart colors match the finish odds and stay stable.
      const grouped: Record<string, DriverDetails[]> = Object.fromEntries(drivers.map((d) => [d, []]));
      rows.forEach(({ Driver, ...race }) => {
        (grouped[Driver] ??= []).push(race);
      });
      return grouped;
    },
    enabled: open && drivers.length >= 1,
    staleTime: Infinity,
  });

  const rankedDrivers = useMemo(
    () => [...(matchup.data?.drivers ?? [])].sort((a, b) => b.groupWinProb - a.groupWinProb),
    [matchup.data],
  );

  const h2h = useMemo(() => {
    const m = new Map<string, number>();
    (matchup.data?.headToHead ?? []).forEach((h) => m.set(`${h.driverA}|${h.driverB}`, h.probAAhead));
    return m;
  }, [matchup.data]);

  // What the tables show: our numbers, or book-style prices in book mode. "Check a price" always uses ours.
  const displayDrivers = useMemo(
    () => (bookMode ? rankedDrivers.map(bookMatchupDriver) : rankedDrivers),
    [bookMode, rankedDrivers]
  );
  const displayH2h = useMemo(() => {
    if (!bookMode) return h2h;
    const m = new Map<string, number>();
    h2h.forEach((p, k) => m.set(k, bookHeadToHead({ driverA: '', driverB: '', probAAhead: p }).probAAhead));
    return m;
  }, [bookMode, h2h]);

  // "Check a price": every head-to-head (both directions) and, for groups, each driver as best of the group.
  const { data: uncertainty } = useQuery<ProbUncertaintyBand[], Error>({
    queryKey: ['probUncertainty'],
    queryFn: getProbUncertainty,
    staleTime: 6 * 60 * 60 * 1000,
  });
  const markets = useMemo(() => {
    // Each market also carries its bet as legs, so it can go on the Slip Builder.
    const out: { key: string; label: string; prob: number; legs: ParlayLeg[] }[] = [];
    if (!isHeadToHead) {
      rankedDrivers.forEach((d) =>
        out.push({
          key: `group|${d.driver}`,
          label: `${d.driver} best of the group`,
          prob: d.groupWinProb,
          legs: [
            {
              kind: 'ahead_of',
              driver: d.driver,
              others: rankedDrivers.map((o) => o.driver).filter((o) => o !== d.driver),
            },
          ],
        }),
      );
    }
    rankedDrivers.forEach((a) =>
      rankedDrivers.forEach((b) => {
        if (a.driver !== b.driver) {
          out.push({
            key: `h2h|${a.driver}|${b.driver}`,
            label: `${a.driver} finishes ahead of ${b.driver}`,
            prob: h2h.get(`${a.driver}|${b.driver}`) ?? 0,
            legs: [{ kind: 'ahead_of', driver: a.driver, others: [b.driver] }],
          });
        }
      }),
    );
    return out;
  }, [rankedDrivers, h2h, isHeadToHead]);
  const [marketKey, setMarketKey] = useState('');
  const market = markets.find((m) => m.key === marketKey) ?? markets[0];
  const slipCount = useSlipCount(site);

  const prob = (p: number) => (showOdds ? toAmericanOdds(p) : formatPct(p));
  const probCellSx = (p: number) => ({
    backgroundColor: getCellColor(p, 'probability'),
    color: 'black',
    fontWeight: 'medium',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap' as const,
  });

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '95%',
          maxWidth: 1400,
          ...maxScreenHeight(90),
          ...modalScrollPaper,
          p: 0,
        }}
      >
        <ModalHeader
          title={`${isHeadToHead ? 'Head to head' : 'Group matchup'}: ${drivers.join(' vs ')}`}
          onClose={onClose}
          compact={scrolled}
        />

        <Box onScroll={onScroll} sx={{ ...modalScrollBody, bgcolor: bodyBg, px: 3, pt: 2, pb: 3, borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Chip label={site} color="primary" size="small" />
          {model === 'layered' && (
            <Chip label="Track Specialists" color="primary" variant="outlined" size="small" />
          )}
          <Chip label={`${nSims.toLocaleString()} simulations`} variant="outlined" size="small" />
          <Chip label={`Data through ${asOf}`} variant="outlined" size="small" />
          {excludeTracks.length > 0 && (
            <Tooltip title={excludeTracks.join(', ')}>
              <Chip
                label={`${excludeTracks.length} track${excludeTracks.length === 1 ? '' : 's'} removed`}
                variant="outlined"
                size="small"
              />
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <BookModeToggle on={bookMode} onChange={setBookMode} />
          <OddsToggle checked={showOdds} onChange={setShowOdds} />
        </Stack>
        <Box sx={{ mb: 1 }}>
          <SimMatchupSummary
            site={site}
            asOf={asOf}
            model={model}
            excludeTracks={excludeTracks}
            matchup={matchup.data}
          />
        </Box>

        <Box
          sx={{
            mt: 1,
            display: 'grid',
            columnGap: 3,
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'minmax(0, 1fr) 340px',
            },
            gridTemplateAreas: {
              xs: '"main" "side" "races"',
              // "side" spans both rows so "Check a price" runs the full height of the modal instead of
              // stopping at the matchup table and leaving a gap when it's shorter than the race history below.
              md: '"main side" "races side"',
            },
          }}
        >
          <Box sx={{ gridArea: 'main', minWidth: 0 }}>
            {/* Matchup odds */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {isHeadToHead ? 'Who finishes ahead' : 'Best finisher in the group'}
            </Typography>
            {matchup.isLoading && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Simulating {nSims.toLocaleString()} races… (usually 5–15 seconds)
                </Typography>
              </Stack>
            )}
            {matchup.error && <Alert severity="error">{matchup.error.message}</Alert>}
            {matchup.data && (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Driver</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          <Tooltip
                            title={
                              isHeadToHead
                                ? 'Chance of finishing ahead of the other driver'
                                : 'Chance of finishing ahead of every other driver in the group'
                            }
                          >
                            <span>{isHeadToHead ? 'Finishes ahead' : 'Best of group'}</span>
                          </Tooltip>
                        </TableCell>
                        {!isHeadToHead && (
                          <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            <Tooltip title="Average place within this group (1 = best)">
                              <span>Avg group place</span>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          Avg Fin
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Win
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          Top 5
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          Top 10
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayDrivers.map((d) => (
                        <TableRow key={d.driver}>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{d.driver}</TableCell>
                          <TableCell align="right" sx={probCellSx(d.groupWinProb)}>
                            {prob(d.groupWinProb)}
                          </TableCell>
                          {!isHeadToHead && (
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {d.avgGroupRank.toFixed(2)}
                            </TableCell>
                          )}
                          <TableCell
                            align="right"
                            sx={{
                              backgroundColor: getCellColor(d.avgFinish, 'position'),
                              color: 'black',
                              fontWeight: 'medium',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {d.avgFinish.toFixed(1)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {prob(d.winProb)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {prob(d.top5Prob)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {prob(d.top10Prob)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {!isHeadToHead && (
                  <>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                      Head-to-head grid
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Chance the row driver finishes ahead of the column driver.
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell />
                            {displayDrivers.map((col) => (
                              <TableCell
                                key={col.driver}
                                align="center"
                                sx={{
                                  fontWeight: 'bold',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <Tooltip title={col.driver}>
                                  <span>{lastName(col.driver)}</span>
                                </Tooltip>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayDrivers.map((row) => (
                            <TableRow key={row.driver}>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.driver}</TableCell>
                              {displayDrivers.map((col) => {
                                if (row.driver === col.driver) {
                                  return (
                                    <TableCell key={col.driver} align="center" sx={{ color: 'text.disabled' }}>
                                      —
                                    </TableCell>
                                  );
                                }
                                const p = displayH2h.get(`${row.driver}|${col.driver}`) ?? 0;
                                return (
                                  <TableCell key={col.driver} align="center" sx={probCellSx(p)}>
                                    {prob(p)}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Re-simulated with the same driver ratings and track weights as the overview. Figures can differ
                  slightly from the overview table because of random sampling.
                </Typography>
              </>
            )}
          </Box>

          {/* Price checker: a sticky sidebar on desktop, below the tables on small screens */}
          <Box sx={{ gridArea: 'side', mt: 2 }}>
            <Box
              sx={{
                position: { md: 'sticky' },
                top: { md: 0 },
                // The modal itself scrolls (maxHeight 90vh below); keep the price panel from running off the
                // bottom of that scroll area by scrolling it internally once it's taller than the viewport.
                maxHeight: { md: 'calc(90vh - 48px)' }, // desktop only, where vh is the visible height
                overflowY: { md: 'auto' },
              }}
            >
              {matchup.data && market ? (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Check a price
                  </Typography>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Market"
                    value={market.key}
                    onChange={(e) => setMarketKey(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    {markets.map((m) => (
                      <MenuItem key={m.key} value={m.key}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <BetValuePanel
                    key={market.key}
                    prob={market.prob}
                    // Matchups aren't backtested directly; use the top-10 market's bands at the same probability.
                    bandPts={bandFor(uncertainty, 'h2h', market.prob)}
                  bookMarket="h2h"
                    onAddToSlip={(oddsText, stakeText) => {
                      const bet = betFromLegs(market.legs, oddsText, stakeText);
                      return !!bet && addToSlip(site, bet);
                    }}
                    slipCount={slipCount}
                  />
                </Paper>
              ) : (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Check a price
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available once the matchup is simulated.
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>

          <Box sx={{ gridArea: 'races', minWidth: 0 }}>
            {races.error ? (
              <Alert severity="error">{races.error.message}</Alert>
            ) : (
              <>
                {!races.isLoading && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                      Data explorer
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <RaceCharts
                        driversDetails={races.data ?? {}}
                        trackKey={trackKey}
                        model={model}
                        finishOdds={finishOdds}
                        nSims={nSims}
                      />
                    </Paper>
                  </>
                )}
                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                  Race by race
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Every race that fed these drivers' ratings, newest first and grouped by race. Sim Weight is each race's
                  share of that driver's rating.
                </Typography>
                <RaceHistoryTable driversDetails={races.data ?? {}} isLoading={races.isLoading} />
              </>
            )}
          </Box>
        </Box>
        </Box>
      </Paper>
    </Modal>
  );
};
